/**
 * Regional store suggestion (AU / UK / US)
 *
 * - Works out which regional store the visitor is on (by the active market / flag, then hostname).
 * - Detects the visitor's real country (IP based) in the browser.
 * - If a different store suits them better, shows a "Go to X / Stay on Y"
 *   card. It never redirects on its own.
 * - The card is persistent: no close (x) button, Escape does not hide it and it
 *   is shown again on every page the visitor navigates to, until they choose
 *   "Go to ... store" or "Stay on this site".
 *
 * Config comes from window.storeSuggestionConfig (snippets/store-suggestion.liquid).
 *
 * Testing: add ?dd_geo=GB (any ISO country code) to the URL to simulate a
 * visitor from that country. Dismissals are ignored while ?dd_geo is present.
 */
(function () {
  'use strict';

  var cfg = window.storeSuggestionConfig;

  // Older snippet versions output { currentCountry, countries: { AU: [name, slug] }, dismissDays }
  // instead of { sites, localizationCountry, ... }. Convert it so the card still works.
  if (cfg && !cfg.sites && cfg.countries) {
    var legacyUrls = {
      au: 'https://www.designerdoorware.com.au',
      uk: 'https://www.designerdoorware.co.uk',
      us: 'https://www.designerdoorware.com'
    };
    var legacyKeys = { AU: 'au', GB: 'uk', UK: 'uk', US: 'us' };
    var legacySites = {};
    Object.keys(cfg.countries).forEach(function (code) {
      var key = legacyKeys[code.toUpperCase()];
      var entry = cfg.countries[code];
      if (key) legacySites[key] = { label: (entry && entry[0]) || code, url: legacyUrls[key] };
    });
    cfg = {
      sites: legacySites,
      localizationCountry: cfg.currentCountry,
      preservePath: true,
      dismissDays: cfg.dismissDays
    };
  }

  if (!cfg || !cfg.sites) return;

  // Run only once per page, even if the script is loaded twice.
  if (window.__ddStoreSuggestionLoaded) return;
  window.__ddStoreSuggestionLoaded = true;

  // Don't show inside the theme editor or to crawlers.
  if (window.Shopify && window.Shopify.designMode) return;
  if (/bot|crawl|spider|lighthouse|headless/i.test(navigator.userAgent || '')) return;

  var STORAGE_DISMISS = 'dd_store_suggestion_dismissed_v2';
  var STORAGE_GEO = 'dd_store_suggestion_geo';
  var GEO_TTL_MS = 24 * 60 * 60 * 1000; // re-check the visitor's country every 24 hours

  // ---- Visitor country -> store --------------------------------------------
  // AU -> AU store, UK/GB -> UK store, everyone else -> US store.
  function siteForCountry(code) {
    if (!code) return null;
    code = code.toUpperCase();

    if (code === 'AU') {
      return 'au';
    } else if (code === 'GB' || code === 'UK') {
      return 'uk';
    } else {
      return 'us';
    }
  }

  var SITE_COUNTRY = { au: 'AU', uk: 'GB', us: 'US' };

  // ---- Current store --------------------------------------------------------
  function hostOf(url) {
    try {
      return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  function currentSite() {
    // 1. The active market (the same one the header flag shows) wins.
    var cookie = document.cookie.match(/(?:^|;\s*)localization=([A-Za-z]{2})/);
    var market = String(
      (cookie && cookie[1]) || cfg.localizationCountry || (window.Shopify && window.Shopify.country) || ''
    ).toUpperCase();
    if (market === 'AU') return 'au';
    if (market === 'GB' || market === 'UK') return 'uk';
    if (market === 'US') return 'us';

    // 2. Otherwise match the hostname against the configured store URLs.
    var host = window.location.hostname.toLowerCase().replace(/^www\./, '');
    var keys = Object.keys(cfg.sites);
    for (var i = 0; i < keys.length; i++) {
      if (hostOf(cfg.sites[keys[i]].url) === host) return keys[i];
    }
    return null;
  }

  // ---- Storage helpers (private mode safe) ----------------------------------
  function readJSON(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { }
  }

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) { }
  }

  // Only the most recent choice is stored: a single { key, until } record that is
  // overwritten each time, never a list of earlier selections.
  function isDismissed(key) {
    var d = readJSON(STORAGE_DISMISS);
    return !!(d && d.key === key && d.until > Date.now());
  }

  function dismiss(key) {
    writeJSON(STORAGE_DISMISS, {
      key: key,
      until: Date.now() + (Number(cfg.dismissDays) || 30) * 24 * 60 * 60 * 1000
    });
  }

  // ---- Visitor country detection ---------------------------------------------
  function fetchWithTimeout(url, ms) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (ctrl) ctrl.abort();
    }, ms);
    return fetch(url, { cache: 'no-store', signal: ctrl ? ctrl.signal : undefined })
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res;
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  }

  // Each detector resolves to a 2-letter code or rejects.
  var DETECTORS = [
    // Same-origin Cloudflare trace (no third party involved).
    function () {
      return fetchWithTimeout('/cdn-cgi/trace', 2500)
        .then(function (r) { return r.text(); })
        .then(function (t) {
          var m = /^loc=([A-Z]{2})$/m.exec(t);
          if (!m || m[1] === 'XX' || m[1] === 'T1') throw new Error('no loc');
          return m[1];
        });
    },
    function () {
      return fetchWithTimeout('https://get.geojs.io/v1/ip/country.json', 3500)
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (!j || !/^[A-Za-z]{2}$/.test(j.country || '')) throw new Error('no country');
          return j.country.toUpperCase();
        });
    },
    function () {
      return fetchWithTimeout('https://api.country.is/', 3500)
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (!j || !/^[A-Za-z]{2}$/.test(j.country || '')) throw new Error('no country');
          return j.country.toUpperCase();
        });
    }
  ];

  function detectCountry() {
    var override = /[?&]dd_geo=([A-Za-z]{2})/.exec(window.location.search);
    if (override) return Promise.resolve({ code: override[1].toUpperCase(), forced: true });

    var cached = readJSON(STORAGE_GEO);
    if (cached && cached.code && cached.at && Date.now() - cached.at < GEO_TTL_MS) {
      return Promise.resolve({ code: cached.code, forced: false });
    }

    return DETECTORS.reduce(function (chain, detect) {
      return chain.catch(detect);
    }, Promise.reject(new Error('start'))).then(function (code) {
      writeJSON(STORAGE_GEO, { code: code, at: Date.now() });
      return { code: code, forced: false };
    });
  }

  // ---- UI -------------------------------------------------------------------
  function countryName(code) {
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
      }
    } catch (e) { }
    return code;
  }

  function targetUrl(site) {
    var base = cfg.sites[site].url.replace(/\/+$/, '');
    if (!cfg.preservePath) return base + '/';
    var search = window.location.search
      .replace(/([?&])dd_geo=[^&]*&?/i, '$1')
      .replace(/[?&]$/, '');
    return base + window.location.pathname + search + window.location.hash;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function render(current, target, visitorCode, forced) {
    if (document.getElementById('StoreSuggestion') || document.querySelector('.store-suggestion')) return;

    // Short store names used in the card copy (e.g. "US store", "Australian store").
    var storeNames = { au: 'Australian', us: 'US', uk: 'UK' };
    var targetLabel = storeNames[target] || cfg.sites[target].label;
    var currentLabel = storeNames[current] || cfg.sites[current].label;
    var from = countryName(visitorCode);
    if (from === 'United States' || from === 'United Kingdom') from = 'the ' + from;

    var box = el('div', 'store-suggestion');
    box.id = 'StoreSuggestion';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'false');
    box.setAttribute('aria-labelledby', 'StoreSuggestionTitle');
    box.setAttribute('aria-describedby', 'StoreSuggestionText');
    box.hidden = true;

    var title = el('h2', 'store-suggestion__title', 'Visit our ' + targetLabel + ' store?');
    title.id = 'StoreSuggestionTitle';

    var text = el(
      'p',
      'store-suggestion__text',
      'It looks like you\u2019re browsing from ' + from + '. Our ' + targetLabel +
        ' store shows local pricing, availability, and shipping.'
    );
    text.id = 'StoreSuggestionText';

    var actions = el('div', 'store-suggestion__actions');

    var go = el('a', 'store-suggestion__btn store-suggestion__btn--primary', 'Go to ' + targetLabel + ' store');
    go.href = targetUrl(target);
    go.setAttribute('rel', 'noopener');

    var stay = el('button', 'store-suggestion__btn store-suggestion__btn--secondary', 'Stay on ' + currentLabel + ' store');
    stay.type = 'button';

    actions.appendChild(go);
    actions.appendChild(stay);
    box.appendChild(title);
    box.appendChild(text);
    box.appendChild(actions);
    document.body.appendChild(box);

    function hide() {
      box.classList.remove('is-visible');
      setTimeout(function () { box.hidden = true; }, 250);
    }

    function stayHere() {
      if (!forced) dismiss(current + '>' + target);
      hide();
    }

    // "Go to" switches the Shopify market first (same mechanism as the header
    // country selector), so the flag, prices and currency change and the popup
    // does not come back. If no localization form exists, the normal link
    // (the store URL) is followed instead.
    go.addEventListener('click', function (e) {
      // A newly selected market replaces any earlier "Stay" choice.
      try { window.localStorage.removeItem(STORAGE_DISMISS); } catch (err) { }
      var input = document.querySelector('form[action*="/localization"] input[name="country_code"]');
      var form = input && input.closest('form');
      if (!form) return;
      e.preventDefault();
      input.value = SITE_COUNTRY[target];
      // Update the header flag straight away, before the page reloads.
      if (typeof window.ddSetMarketFlag === 'function') {
        window.ddSetMarketFlag(SITE_COUNTRY[target]);
      } else {
        var flags = document.querySelectorAll('.disclosure > .disclosure__button, .selected-option');
        for (var i = 0; i < flags.length; i++) flags[i].setAttribute('data-country', SITE_COUNTRY[target]);
      }
      form.submit();
    });

    // "Stay on this site" is the only way to hide the card without switching
    // stores (no close button, no Escape, no outside click).
    stay.addEventListener('click', stayHere);

    box.hidden = false;
    // Next frame so the transition runs.
    requestAnimationFrame(function () { box.classList.add('is-visible'); });
  }

  // ---- Init -----------------------------------------------------------------
  function init() {
    var current = currentSite();
    // Inspect in the browser console: storeSuggestionDebug
    var dbg = (window.storeSuggestionDebug = { currentSite: current, country: null, target: null, result: 'pending' });
    if (!current) {
      dbg.result = 'no current site (hostname not matched)';
      return;
    }

    detectCountry()
      .then(function (geo) {
        var target = siteForCountry(geo.code);
        dbg.country = geo.code;
        dbg.target = target;
        if (!target || target === current || !cfg.sites[target]) {
          dbg.result = 'not shown: suggested store is the current store';
          return;
        }
        if (!geo.forced && isDismissed(current + '>' + target)) {
          dbg.result = 'not shown: dismissed earlier (clear localStorage key dd_store_suggestion_dismissed_v2)';
          return;
        }
        render(current, target, geo.code, geo.forced);
        dbg.result = 'shown';
      })
      .catch(function () {
        dbg.result = 'not shown: country detection failed';
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
